import * as React from 'react';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';

import { IRegionalAndLanguageSettingsQueryResult } from '../../Language/hooks/useGetRegionalAndLanguageSettingQuery';

export interface IPdfDocumentProps {
  readonly file: Uint8Array;
  readonly getRegionalAndLanguageSettingsQuery?: IRegionalAndLanguageSettingsQueryResult;
}

let pdfDocumentInstanceCount = 0;

export const PdfDocument: React.FC<IPdfDocumentProps> = ({ file, getRegionalAndLanguageSettingsQuery }) => {
  const [t] = useLocalization('TermsOfUse');

  React.useEffect(() => {
    function handleWebViewerLoaded(event: CustomEvent) {
      event.detail.source.PDFViewerApplicationOptions.set('disablePreferences', true);
      event.detail.source.PDFViewerApplicationOptions.set('externalLinkTarget', 2); // 2 means target="_blank"

      if (getRegionalAndLanguageSettingsQuery?.defaultDisplayLanguage) {
        event.detail.source.PDFViewerApplicationOptions.set(
          'locale',
          getRegionalAndLanguageSettingsQuery.defaultDisplayLanguage,
        );
      }
    }

    pdfDocumentInstanceCount++;
    if (pdfDocumentInstanceCount === 1) {
      window.addEventListener('webviewerloaded', handleWebViewerLoaded as EventListener);
    }

    return () => {
      pdfDocumentInstanceCount--;

      if (pdfDocumentInstanceCount === 0) {
        window.removeEventListener('webviewerloaded', handleWebViewerLoaded as EventListener);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Specifically not including 'locale' as a dependency
  }, []);

  // Trident / MSHTML is the browser engine used in IE11
  const isTrident = navigator.userAgent.includes('Trident/');
  // WebView is the browser engine used in Edge and Windows Account Manager (WAM)
  const isWebView = navigator.userAgent.includes('Edge/');
  const legacyPdfjsViewer = isTrident || isWebView;
  const pdfjsLib = legacyPdfjsViewer ? 'pdfjs-generic-es5' : 'pdfjs-generic';

  const onLoad = (event: any) => {
    const window = event.target.contentWindow;
    const elementsToHide = [
      'openFile',
      'secondaryOpenFile',
      'editorModeButtons',
      'editorModeSeparator',
    ];

    elementsToHide.forEach(id => {
      const element = window.document.getElementById(id);
      if (element) {
        element.style.display = 'none';
      }
    });

    window.addEventListener('dragover', (evt: Event) => {
      evt.preventDefault();
      evt.stopPropagation();
    }, { capture: true });

    window.addEventListener('drop', (evt: Event) => {
      evt.preventDefault();
      evt.stopPropagation();
    }, { capture: true });

    window.PDFViewerApplication.initializedPromise.then(() => {
      if (legacyPdfjsViewer) {
        window.PDFViewerApplication.open(file);
      } else {
        window.PDFViewerApplication.open({ data: file });
      }
    });
  };

  return getRegionalAndLanguageSettingsQuery?.isLoading ? null : (
    <iframe
      aria-label={t('pdfViewer')}
      src={`/${pdfjsLib}/web/viewer.html?file=`}
      width={'100%'}
      height={'100%'}
      onLoad={(event) => onLoad(event)}
    ></iframe>
  );
};
